import i18n from 'i18next'
import classNames from 'classnames'
import { useState, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { Select } from 'antd'
import PreloaderBlock from 'components/PreloaderBlock/PreloaderBlock'
import { parseThousandsGroups } from 'helpers/numbers'
import DashboardService from 'redux/middlewares/dashboard'
import UsersNotificationsService from 'redux/middlewares/usersNotifications'
import { topsTypes, topsTypesLabels } from './constants/topsTypes'
import baseStyles from './styles/base.module.scss'
import selectStyles from './styles/headerSelect.module.scss'
import tableStyle from './styles/topTable.module.scss'

const TopSKUPerArea = ({ globalStartDate, globalFinishDate }) => {
  const { t } = useTranslation()
  const [responseType, setResponseType] = useState(topsTypes.COUNT)
  const [selectedAreaId, setSelectedAreaId] = useState(null)

  // Load delivery areas for dropdown
  const { data: areasData, isFetching: isAreasFetching } = useQuery(
    ['delivery-areas'],
    async () => {
      const response = await UsersNotificationsService.getNotificationDeliveryAreas()
      const data = response.data.data || response.data
      // Handle both array and { result: Array } response formats
      return Array.isArray(data) ? data : data.result || []
    },
    {
      staleTime: 1000 * 60 * 30, // 30 minutes
    },
  )

  // Load top SKU per area data
  const { data: topSKUPerAreaData, isFetching: isTopSKUPerAreaFetching } = useQuery(
    ['dashboard-top-sku-per-area', globalStartDate, globalFinishDate, responseType, selectedAreaId],
    async () => {
      const response = await DashboardService.getTopSKUPerAreaStats({
        startDate: globalStartDate.toISOString(),
        finishDate: globalFinishDate.toISOString(),
        responseType,
        areaId: selectedAreaId,
      })

      return response.data.data
    },
    {
      staleTime: 1000 * 60 * 10, // 10 minutes
      refetchOnMount: 'always',
    },
  )

  // Get products for the selected area
  const selectedAreaProducts = useMemo(() => {
    if (!topSKUPerAreaData || topSKUPerAreaData.length === 0) return []

    if (selectedAreaId) {
      const area = topSKUPerAreaData.find((a) => a.area_id === selectedAreaId)
      return area?.products || []
    }

    // If no area selected, show top 10 from all areas combined
    const allProducts = topSKUPerAreaData.flatMap((area) => area.products)
    const productMap = new Map()

    for (const product of allProducts) {
      const key = product.product_id
      if (productMap.has(key)) {
        const existing = productMap.get(key)
        if (responseType === topsTypes.COUNT) {
          existing.count = (existing.count || 0) + (product.count || 0)
        } else {
          existing.revenue = (existing.revenue || 0) + (product.revenue || 0)
        }
      } else {
        productMap.set(key, { ...product })
      }
    }

    const sortedProducts = Array.from(productMap.values()).sort((a, b) => {
      if (responseType === topsTypes.COUNT) {
        return (b.count || 0) - (a.count || 0)
      }
      return (b.revenue || 0) - (a.revenue || 0)
    })

    return sortedProducts.slice(0, 10)
  }, [topSKUPerAreaData, selectedAreaId, responseType])

  // Transform areas for Select options
  const areaOptions = useMemo(() => {
    if (!areasData || !Array.isArray(areasData)) return []
    return [
      { value: null, label: t('dashboard.all areas') },
      ...areasData?.map((area) => ({
        value: area.id,
        label: i18n.language === 'ar' ? area.arabianName || area.name : area.name,
      })),
    ]
  }, [areasData, t])

  const isLoading = isAreasFetching || isTopSKUPerAreaFetching

  return (
    <div className={baseStyles.chart}>
      <div className={baseStyles.chart__header}>
        <div className={baseStyles.chart__headerTop}>
          <p className={baseStyles.chart__headerTitle}>{t('dashboard.top sku per area')}</p>
          <div className={'field-wrapper'} style={{ display: 'flex', gap: '10px' }}>
            <Select
              className={selectStyles.headerSelect}
              options={areaOptions}
              value={selectedAreaId}
              onChange={(areaId) => setSelectedAreaId(areaId)}
              placeholder={t('dashboard.select area')}
              loading={isAreasFetching}
              style={{ minWidth: 150 }}
            />
            <Select
              className={selectStyles.headerSelect}
              options={[
                { value: topsTypes.COUNT, label: t(topsTypesLabels.COUNT) },
                { value: topsTypes.REVENUE, label: t(topsTypesLabels.REVENUE) },
              ]}
              value={responseType}
              onChange={(topsType) => setResponseType(topsType)}
            />
          </div>
        </div>
      </div>
      <div className={baseStyles.chart__body}>
        {!isLoading && selectedAreaProducts.length > 0 ? (
          <div
            className={classNames([tableStyle.table, tableStyle.table_stickyHead])}
            style={{ maxHeight: 289 }}
          >
            <table>
              <thead>
                <tr>
                  <th className={tableStyle.rankColumn}>#</th>
                  <th className={tableStyle.promotionColumn}>SKU</th>
                  <th className={tableStyle.codeColumn}>{t('dashboard.sku_code')}</th>
                  <th className={tableStyle.valueColumn}>{t(topsTypesLabels[responseType])}</th>
                </tr>
              </thead>
              <tbody>
                {selectedAreaProducts.map((productStat, index) => {
                  return (
                    <tr key={productStat.product_id}>
                      <td className={tableStyle.rankCell}>{index + 1}</td>
                      <td className={tableStyle.promotionCell}>
                        {i18n.language === 'ar'
                          ? productStat.product_arabian_title || productStat.product_title
                          : productStat.product_title}
                      </td>
                      <td className={tableStyle.codeCell}>
                        <span className={tableStyle.promotionCode}>
                          {productStat.product_sap_id || 'N/A'}
                        </span>
                      </td>
                      {responseType === topsTypes.COUNT && (
                        <td className={tableStyle.valueCell}>{productStat.count}</td>
                      )}
                      {responseType === topsTypes.REVENUE && (
                        <td className={tableStyle.valueCell}>
                          {parseThousandsGroups(productStat.revenue)}
                        </td>
                      )}
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        ) : isLoading ? (
          <PreloaderBlock minHeight={248} />
        ) : (
          <div style={{ textAlign: 'center', padding: '20px' }}>
            {t('dashboard.no data available')}
          </div>
        )}
      </div>
    </div>
  )
}

export default TopSKUPerArea
